/** Planer-spezifische Helper, die nicht in @vimaana/ui gehören. */

export type PlanerStatus = "geplant" | "laeuft" | "abgeschlossen" | "abgerechnet";

/** Termin-Status aus Datum ableiten (vereinfachte UX im Mobile). */
export function terminStatusFromDate(startIso: string, status?: string): PlanerStatus {
  if (status === "abgerechnet") return "abgerechnet";
  const d = new Date(startIso);
  if (Number.isNaN(d.getTime())) return "geplant";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (dDay.getTime() < today.getTime()) return "abgeschlossen";
  if (dDay.getTime() === today.getTime()) return "laeuft";
  return "geplant";
}

/**
 * Netto-Stunden eines Slots — client-seitig direkt aus start/end/pause
 * berechnet. Identisch zur Server-Formel in _slot_hours, aber ohne
 * Refetch-Latenz: ändert sich start_at, end_at oder pause_stunden, ist
 * der Wert sofort konsistent.
 */
export function slotStundenNetto(
  slot: { start_at: string; end_at: string; pause_stunden?: number },
): number {
  if (!slot.start_at || !slot.end_at) return 0;
  const start = new Date(slot.start_at).getTime();
  const end = new Date(slot.end_at).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  const grossH = (end - start) / 3_600_000;
  return Math.max(0, grossH - (slot.pause_stunden || 0));
}

/**
 * Personalkosten eines Slots — Stunden × Lohn + Zusatzkosten.
 *
 * Inhaber haben stundenlohn = 0; ihre Stunden zählen nicht zu den Kosten.
 * Auch nicht-bestätigte Slots werden mit eingerechnet (Plan-Kosten).
 */
export function slotKosten(
  slot: {
    start_at: string; end_at: string;
    pause_stunden?: number; stundenlohn?: number; zusatzkosten?: number;
  },
): number {
  const stunden = slotStundenNetto(slot);
  return stunden * (slot.stundenlohn || 0) + (slot.zusatzkosten || 0);
}

/** Summe der Personalkosten aller Slots eines Termins. */
export function terminKosten(
  termin: {
    slots: Array<{
      start_at: string; end_at: string;
      pause_stunden?: number; stundenlohn?: number; zusatzkosten?: number;
    }>;
  },
): number {
  return termin.slots.reduce((s, sl) => s + slotKosten(sl), 0);
}

/** EUR-Format konsistent über die App. */
export function formatEur(n: number, opts?: { round?: boolean }): string {
  const round = opts?.round ?? false;
  return n.toLocaleString("de-DE", {
    style: "currency", currency: "EUR",
    minimumFractionDigits: round ? 0 : (n < 100 ? 2 : 0),
    maximumFractionDigits: round ? 0 : 2,
  });
}

/** Standard-Tabs der Planer-App. */
export const PLANER_TABS = [
  { id: "aktivitaet", label: "Aktiv.",  ic: "◔", to: "/aktivitaet" },
  { id: "tag",       label: "Tag",      ic: "✓", to: "/tag" },
  { id: "monat",     label: "Monat",    ic: "▤", to: "/monat" },
  { id: "projekte",  label: "Projekte", ic: "◉", to: "/projekte" },
  { id: "profil",    label: "Profil",   ic: "◌", to: "/profil" },
];
